/**
 * Alert component para mostrar mensajes de éxito/error/warning
 */

import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../../utils';

type AlertType = 'error' | 'success' | 'info' | 'warning';

interface AlertAction {
    label: string;
    onClick: () => void;
}

interface AlertProps {
    /** Contenido del alert (si se usa como wrapper) */
    children?: ReactNode;
    /** Tipo/variante del alert */
    type?: AlertType;
    /** @deprecated Usar `type` en su lugar */
    variant?: AlertType;
    /** Mensaje a mostrar (alternativa a children) */
    message?: string;
    /** Acción opcional con botón */
    action?: AlertAction;
    /** Clases CSS adicionales */
    className?: string;
}

const alertVariants = cva(
    'flex items-center justify-between gap-4 rounded-xl border px-4 py-3 text-sm leading-6',
    {
        variants: {
            type: {
                error: 'border-error/25 bg-error-bg text-error',
                success: 'border-success/25 bg-success-bg text-success',
                info: 'border-info/25 bg-info-bg text-info',
                warning: 'border-amber-400/35 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
            },
        },
        defaultVariants: {
            type: 'info',
        },
    }
);

export function Alert({
    children,
    type,
    variant = 'info',
    message,
    action,
    className = ''
}: AlertProps) {
    const alertType = type || variant;
    const content = message || children;

    return (
        <div
            className={cn(alertVariants({ type: alertType }), className)}
            role="alert"
        >
            <span className="flex-1">{content}</span>
            {action && (
                <button
                    type="button"
                    className="rounded-md px-2 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
                    onClick={action.onClick}
                >
                    {action.label}
                </button>
            )}
        </div>
    );
}
